/**
 * The fields the FOLDER page edits, read from and written back to one folder in
 * `collection.settings.folders[]` (owner D3, 28.09.2026).
 *
 * Stored names (all optional on a folder record):
 *   - `description`       Docs section. The name MCP's `folder_create`, the
 *                         Insomnia export and the importers already use.
 *   - `auth`              Authorization. ABSENT means "Inherit from parent";
 *                         `getFolderAuthChain` skips a folder without `auth`,
 *                         and `resolveEffectiveAuthChain` skips `inherit`, so
 *                         writing nothing for inherit keeps the stored shape the
 *                         importers produce.
 *   - `variables`         Variables (normalised rows, see utils/folderVariables).
 *   - `preRequestScript`  Scripts. The importers' names; `utils/scriptChain`
 *   - `testScript`        reads them under Run collection.
 *
 * Every other key of the folder (name, parent_id, sort_order, anything an
 * importer added) is kept as it is.
 */
import { getFolderById, getFolders, withCollectionFolders } from './collectionFolders';
import { normalizeFolderVariables } from './folderVariables';

export const INHERIT_AUTH = Object.freeze({ type: 'inherit' });

/** Page fields for one folder record (defaults for anything absent). */
export function folderPageFields(folder) {
  const f = folder || {};
  return {
    documentation: typeof f.description === 'string' ? f.description : '',
    auth: f.auth && typeof f.auth === 'object' ? f.auth : INHERIT_AUTH,
    variables: normalizeFolderVariables(f.variables),
    preRequestScript: typeof f.preRequestScript === 'string' ? f.preRequestScript : '',
    testScript: typeof f.testScript === 'string' ? f.testScript : '',
  };
}

function setOrDrop(target, key, value, keep) {
  if (keep) target[key] = value;
  else delete target[key];
}

/**
 * A new collection whose folder `folderId` carries `fields`. Only the keys
 * present in `fields` are touched; an empty script / description or an
 * `inherit` auth REMOVES the key rather than storing an empty value.
 */
export function withFolderPageFields(collection, folderId, fields = {}) {
  if (!getFolderById(collection, folderId)) return collection;
  const folders = getFolders(collection).map((f) => {
    if (String(f.id) !== String(folderId)) return f;
    const next = { ...f };
    if ('documentation' in fields) {
      const text = String(fields.documentation ?? '');
      setOrDrop(next, 'description', text, text !== '');
    }
    if ('auth' in fields) {
      const auth = fields.auth;
      setOrDrop(next, 'auth', auth, !!auth && typeof auth === 'object' && auth.type && auth.type !== 'inherit');
    }
    if ('variables' in fields) next.variables = normalizeFolderVariables(fields.variables);
    for (const key of ['preRequestScript', 'testScript']) {
      if (!(key in fields)) continue;
      const text = String(fields[key] ?? '');
      setOrDrop(next, key, text, text.trim() !== '');
    }
    return next;
  });
  return withCollectionFolders(collection, folders);
}

/** Put the listed keys of one folder back exactly as they were in `previous` (absent stays absent). */
export function restoreFolderKeys(collection, folderId, previous, keys) {
  const folders = getFolders(collection).map((f) => {
    if (String(f.id) !== String(folderId)) return f;
    const next = { ...f };
    for (const key of keys) {
      if (previous && Object.prototype.hasOwnProperty.call(previous, key)) next[key] = previous[key];
      else delete next[key];
    }
    return next;
  });
  return withCollectionFolders(collection, folders);
}

/** The stored keys `withFolderPageFields` may touch for a given `fields` object. */
export function storedKeysFor(fields = {}) {
  const keys = [];
  if ('documentation' in fields) keys.push('description');
  if ('auth' in fields) keys.push('auth');
  if ('variables' in fields) keys.push('variables');
  if ('preRequestScript' in fields) keys.push('preRequestScript');
  if ('testScript' in fields) keys.push('testScript');
  return keys;
}
